import React, { useEffect, useState } from 'react';
import {
  LegendItem,
  LegendItemProps,
} from '@components/progress/chart/LegendItem';
import { LoadingScroll } from 'geniebook-ui/dist';
import ProgressChartFilter, {
  ProgressChartFilterItem,
} from '@components/progress/chart/ProgressChartFilter';
import { topicSkillLevel } from '@components/progress/sheet/SelectedTopic';
import { useMasteryLevels, useProgressScores } from '@hooks/student.hook';
import moment from 'moment';
import { dataPointLogic } from '@components/progress/chart/ChartHelper';
import LineChartMasteryLevel from '@components/progress/chart/LineChartMasteryLevel';
import LineChartScore from '@components/progress/chart/LineChartScore';
import { ITableItemSkillLevel } from '@components/progress/table/TableItem';
import {
  IGetMasteryLevelProgressDataFormData,
  IGetStudentScoreProgressDataFormData,
} from '@models/interfaces/student.interface';

const filterDurationRange: ProgressChartFilterItem<number>[] = [
  {
    label: '30 Days',
    value: 30,
  },
  {
    label: '60 Days',
    value: 60,
  },
  {
    label: '90 Days',
    value: 90,
  },
  {
    label: '180 Days',
    value: 180,
  },
];

export interface ChartSkillLevelProps {
  selectedTopics: topicSkillLevel[];
  haveMasteryLevel: boolean;
  subjectId: number;
  preventLoadingAnimation?: boolean;
  isTestAccount: boolean;
  topics: ITableItemSkillLevel[];
}

const ChartSkillLevel = (props: ChartSkillLevelProps) => {
  const [rangeSelected, setRangeSelected] = useState<number | string>(
    filterDurationRange[0].value,
  );
  const [progressScoreForms, setProgressScoreForms] = useState<
    IGetStudentScoreProgressDataFormData[]
  >([]);
  const [masteryForm, setMasteryForm] =
    useState<IGetMasteryLevelProgressDataFormData>({
      backdate_day_duration: filterDurationRange[0].value,
      topic_ids: '',
      subject_id: props.subjectId,
    });
  const scoreChartData = useProgressScores(progressScoreForms);
  const masteryChartData = useMasteryLevels(masteryForm, props.isTestAccount);
  const now = moment();
  const [xAxisLabel, setXAxisLabel] = useState<string[]>(
    getLineChartXAxisLabel(filterDurationRange[0].value),
  );

  function setLegendFromSelectedTopics() {
    const legends: LegendItemProps[] = [];
    for (let i = 0; i < props.selectedTopics.length; i++) {
      legends.push({
        label: props.selectedTopics[i].label,
        color: props.selectedTopics[i].color,
        id: props.selectedTopics[i].id,
      });
    }
    if (legends.length === 0) {
      legends.push({
        label: 'Total',
        color: '#D5ACF3',
        id: 1,
      });
    }
    return legends;
  }

  const legends: LegendItemProps[] = setLegendFromSelectedTopics();

  useEffect(() => {
    if (!props.haveMasteryLevel) {
      prepareScoreFormData();
    } else {
      prepareMasteryFormData();
    }
  }, [props.selectedTopics, props.haveMasteryLevel, rangeSelected]);
  useEffect(() => {
    setXAxisLabel(getLineChartXAxisLabel(rangeSelected));
  }, [props.haveMasteryLevel]);
  function prepareScoreFormData() {
    const formData: IGetStudentScoreProgressDataFormData[] = [];
    props.selectedTopics.forEach(item => {
      formData.push({
        topic_id: item.id,
        subject_id: props.subjectId,
        backdate_day_duration: rangeSelected,
      });
    });
    if (formData.length === 0) {
      formData.push({
        topic_id: null,
        subject_id: props.subjectId,
        backdate_day_duration: rangeSelected,
      });
    }
    setProgressScoreForms(formData);
  }
  function prepareMasteryFormData() {
    const topicIds: (string | number)[] = [];
    props.selectedTopics.forEach(item => {
      topicIds.push(item.id);
    });

    setMasteryForm({
      subject_id: props.subjectId,
      topic_ids: topicIds.join(','),
      backdate_day_duration:
        typeof rangeSelected === 'string'
          ? parseInt(rangeSelected)
          : rangeSelected,
    });
  }

  function handleChangeRange(value: number | string) {
    setRangeSelected(value);
    setXAxisLabel(getLineChartXAxisLabel(value));
  }

  function getLineChartXAxisLabel(value: number | string) {
    const xAxis: string[] = [];
    const date = now.clone();

    let numericValue: number;
    if (typeof value === 'string') {
      numericValue = parseInt(value);
    } else {
      numericValue = value;
    }

    for (let i = 0; i <= numericValue; i++) {
      if (dataPointLogic(i, numericValue) || props.haveMasteryLevel) {
        xAxis.unshift(date.format('D MMM'));
      }
      date.subtract(1, 'day');
    }
    return xAxis;
  }

  return (
    <div className={'bg-primary-navyblue-shade/[0.65] rounded-2xl py-6 px-5'}>
      <div
        className={
          'flex gap-1 text-neutral-whiteboard font-bold text-md text-center justify-center'
        }
      >
        <span>{props.haveMasteryLevel ? 'Skill Level: ' : 'Score: '}</span>
        <ProgressChartFilter
          options={filterDurationRange}
          setSelection={handleChangeRange}
          selected={rangeSelected}
          label={'Past '}
        />
      </div>
      <div className={'mb-4 relative'}>
        {props.haveMasteryLevel ? (
          <LineChartMasteryLevel
            xAxisLabel={xAxisLabel}
            rawData={masteryChartData}
            rangeSelected={rangeSelected}
            selectedTopics={props.selectedTopics}
            isLoading={masteryChartData.isLoading}
            isTestAccount={props.isTestAccount}
            topics={props.topics}
          />
        ) : (
          <LineChartScore
            xAxisLabel={xAxisLabel}
            rawData={scoreChartData}
            rangeSelected={rangeSelected}
            selectedTopics={props.selectedTopics}
            isLoading={
              scoreChartData.filter(item => item.isLoading == true).length > 0
            }
          />
        )}
        {!props.preventLoadingAnimation &&
        ((masteryChartData.isLoading &&
          props.haveMasteryLevel &&
          !props.isTestAccount) ||
          (scoreChartData.filter(item => item.isLoading == true).length > 0 &&
            !props.haveMasteryLevel)) ? (
          <LoadingScroll
            className={
              'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'
            }
          />
        ) : null}
      </div>
      <div>
        {legends.map(item => {
          return <LegendItem {...item} key={item.id} />;
        })}
      </div>
    </div>
  );
};

export default ChartSkillLevel;
